/* ============================================================================
   plp-description.js — <sf-plp-desc> (sections/main-collection.liquid, Figma 1771:126031 "plp-description")
   Vanilla ES2020, no dependencies beyond window.theme (optional).

   The collection description under the title. The CSS shows three lines on a phone (the Figma text box) and four
   from 768 from the first paint; this decides whether that hides anything. If it all fits: the clamp is lifted
   (.is-fits) and no button shows. Longer: Read more / Read less appears — a real <button> with aria-expanded and
   aria-controls, so Enter and Space work and a screen reader hears the state. The text opens and closes with a
   height tween (none under reduced motion). It only ever grows or shrinks downwards, so nothing above it moves,
   and a second press mid-tween turns it round from the height it has reached. The section's sticky bar re-measures
   itself when the header changes height (assets/facets.js watches .plp-intro).
   ============================================================================ */
(function () {
  'use strict';
  if (customElements.get('sf-plp-desc')) return;

  /* the line count changes at this width (3 → 4), so the fit is checked again across it */
  const WIDE = '(min-width: 768px)';
  const DUR = 320;
  const EASE = 'cubic-bezier(0.2, 0, 0, 1)';

  const reduced = () => {
    const t = window.theme || {};
    if (typeof t.reducedMotion === 'boolean') return t.reducedMotion;
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  };

  class SFPlpDesc extends HTMLElement {
    connectedCallback() {
      this.text = this.querySelector('.plp-desc__text');
      this.btn = this.querySelector('.plp-desc__toggle');
      if (!this.text || !this.btn) return;
      this._ctrl = new AbortController();
      const signal = this._ctrl.signal;
      this._mq = window.matchMedia(WIDE);

      this.btn.addEventListener('click', () => this.toggle(), { signal });
      /* A link under the clamp (line 4 on): open, so it is seen, rather than the clamped box scrolling itself to it
         and staying scrolled. The scrollTop test catches browsers that scroll before focusin, the rect test those
         that scroll after. A link already in view on lines 1–3 leaves the text as it is. */
      this.text.addEventListener('focusin', (e) => {
        if (this.btn.hidden || this.classList.contains('is-open') || this.classList.contains('is-fits')) return;
        if (this.text.scrollTop === 0 && e.target.getBoundingClientRect().bottom <= this.text.getBoundingClientRect().bottom + 1) return;
        this.text.scrollTop = 0;
        this.toggle();
      }, { signal });
      const check = () => this._check();
      this._mq.addEventListener('change', check, { signal });
      /* a rotation or a late font changes how many lines the text takes */
      if ('ResizeObserver' in window) {
        let w = -1;
        this._ro = new ResizeObserver(() => {
          const nw = this.clientWidth;
          if (nw === w) return;
          w = nw;
          check();
        });
        this._ro.observe(this);
      } else {
        window.addEventListener('resize', check, { signal });
      }
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.isConnected && check());
      check();
    }

    disconnectedCallback() {
      if (this._ctrl) this._ctrl.abort();
      if (this._ro) this._ro.disconnect();
      if (this._anim) this._anim.cancel();
    }

    /* Measured in the clamped state; the shopper's open/closed choice (kept in _open, not the class, so a rotation
       through a width where the text fits does not lose it) survives a re-check. */
    _check() {
      /* a rotation or resize mid-tween is not dropped: the check runs again when the tween ends */
      if (this._anim) {
        this._stale = true;
        return;
      }
      this._stale = false;
      const open = !!this._open;
      this.classList.remove('is-open', 'is-fits');
      const over = this.text.scrollHeight > this.text.clientHeight + 1;
      if (!over) {
        this.classList.add('is-fits');
        this.btn.hidden = true;
        this.btn.setAttribute('aria-expanded', 'false');
        return;
      }
      this.btn.hidden = false;
      if (open) this.classList.add('is-open');
      this.btn.setAttribute('aria-expanded', String(open));
    }

    toggle() {
      const t = this.text;
      const open = !this.classList.contains('is-open');
      /* the height it shows right now — mid-tween if a tween is running — is where the next one starts */
      const from = t.getBoundingClientRect().height;
      /* Closing pulls the text up towards the button; on a long description that would carry the focused button
         off the top of the screen. So while it closes the page scrolls with it and the button stays where the
         shopper pressed it ('instant': html.motion-ok makes scrolling smooth). Read before a running tween is
         cancelled, so it is where the button really is on screen. */
      const btnTop = this.btn.getBoundingClientRect().top;
      /* a cancel event arrives later, so the running tween is tidied up here, not in its handler */
      if (this._anim) {
        const old = this._anim;
        this._anim = null;
        old.cancel();
        t.style.overflow = '';
        this.classList.remove('is-closing');
      }
      const hold = () => {
        const d = this.btn.getBoundingClientRect().top - btnTop;
        if (Math.abs(d) >= 1) window.scrollBy({ top: d, behavior: 'instant' });
      };
      this._open = open;
      this.classList.toggle('is-open', open);
      this.btn.setAttribute('aria-expanded', String(open));
      const to = t.getBoundingClientRect().height;
      if (reduced() || typeof t.animate !== 'function' || Math.abs(from - to) < 1) {
        if (!open) hold();
        if (this._stale) this._check();
        return;
      }
      /* closing: keep the full text on screen while the box shrinks; the ellipsis comes back at the end */
      if (!open) this.classList.add('is-closing');
      t.style.overflow = 'hidden';
      const a = t.animate([{ height: from + 'px' }, { height: to + 'px' }], { duration: DUR, easing: EASE });
      this._anim = a;
      if (!open) {
        const follow = () => {
          if (this._anim !== a) return;
          hold();
          requestAnimationFrame(follow);
        };
        requestAnimationFrame(follow);
      }
      a.onfinish = a.oncancel = () => {
        if (this._anim !== a) return;
        this._anim = null;
        t.style.overflow = '';
        this.classList.remove('is-closing');
        if (!open) hold();
        /* after hold(): it reads the button, which a re-check may hide */
        if (this._stale) this._check();
      };
    }
  }

  customElements.define('sf-plp-desc', SFPlpDesc);
})();
